import React, { useState, useContext, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import injectStyles from 'react-jss'
import cn from 'classnames'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import InputAdornment from '@material-ui/core/InputAdornment'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import Checkbox from '@material-ui/core/Checkbox'

import { ButtonInline } from 'Components/Buttons/ButtonInline'
import { SnackBarContext } from 'Components/SnackBar'

import EditIcon from 'Assets/Images/icons/edit'
import CancelIcon from 'Assets/Images/icons/cancel'
import SaveIcon from 'Assets/Images/icons/save'
import { theme } from 'Assets/Styles/theme'
import { showReduxModal } from '../../../../../../../../Reducers/UI/actions'

import styles from './styles.js'
import { useHandleEdit } from '../../../../useHandleEdit'
import {
  WEEKLY_MISSION_LIMIT,
  CITY_SETTINGS_HEADERS,
  BORDER_KEYS,
  CITY_SETTINGS_PRE_HEADERS,
} from '../../../../../../constants'
import {
  checkIsFlyerPack,
  formatCitiesSettings,
  checkDistributionZonesInCity,
} from './utils'
import {
  getComparator,
  isValueValid,
  stableSort,
} from '../../../../../../../../Components/CityMetricTable/utils'

const CitySettingsTable = ({
  classes,
  title = '',
  currencySign = '$',
  citiesSettings,
  countrySettings,
  onSave = () => {},
  isEditingTable = false,
  setIsEditingTable = () => {},
}) => {
  const dispatch = useDispatch()
  const [orderBy, setOrderBy] = useState('name')
  const [order, setOrder] = useState('asc')
  const [citiesSettingsValues, setCitiesSettingsValues] = useState([])
  const [error, setError] = useState(false)

  const runSnackBar = useContext(SnackBarContext)

  const { isEdit, handleEditMode } = useHandleEdit({
    isEditingTable,
    setIsEditingTable,
  })

  const handleRequestSort = property => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleCellChange = async (value, row, key, type) => {
    const isFlyerPack = checkIsFlyerPack(key)

    if (
      (key === 'enabledChannelLetterbox' ||
        key === 'enabledChannelPreciseLocations') &&
      value
    ) {
      const response = await checkDistributionZonesInCity(row.id, key)
      if (response.error) {
        dispatch(
          showReduxModal({
            title: `Can't activate!`,
            onConfirm: () => {},
            children: (
              <div style={{ textAlign: 'center' }}>
                {`Missing Distribution Zone for ${
                  key === 'enabledChannelLetterbox' ? 'D2D' : 'H2H'
                }`}
              </div>
            ),
            confirmLabel: 'OK',
            hideCancel: true,
          }),
        )
        return
      }
    }

    let formattedValue
    if (type === 'checkbox' || isFlyerPack) {
      formattedValue = value
    } else {
      formattedValue = value ? Number(value) : value === '' ? null : 0
    }

    const newCitySettings = citiesSettingsValues.map(city =>
      city.id === row.id
        ? {
            ...city,
            [key]: formattedValue,
          }
        : city,
    )
    const { error } = formatCitiesSettings(newCitySettings, countrySettings)

    setError(error)
    setCitiesSettingsValues(newCitySettings)
  }

  const renderTableCell = (row, key, type) => {
    switch (type) {
      case 'checkbox': {
        // const {
        //   enabledChannelPreciseLocations,
        //   enabledChannelBroadAreas,
        //   enabledChannelLetterbox,
        // } = countrySettings
        let isDisabled = false
        // if (key === 'enabledChannelPreciseLocations') {
        //   isDisabled = !enabledChannelPreciseLocations
        // }

        // if (key === 'enabledChannelBroadAreas') {
        //   isDisabled = !enabledChannelBroadAreas
        // }

        // if (key === 'enabledChannelLetterbox') {
        //   isDisabled = !enabledChannelLetterbox
        // }

        return isEdit ? (
          <Checkbox
            checked={row[key]}
            onChange={(event, checked) =>
              handleCellChange(checked, row, key, type)
            }
            disabled={isDisabled}
          />
        ) : (
          <Checkbox checked={row[key]} disabled={true} />
        )
      }
      case 'currencySign': {
        if (isEdit) {
          const error = key === 'baPayout' && row[key] === 0
          return (
            <OutlinedInput
              id={`${row.name}-${key}`}
              value={row[key] || isEdit ? row[key] : countrySettings[key]}
              onChange={event => {
                const { value } = event.target

                return isValueValid(value)
                  ? handleCellChange(value, row, key)
                  : null
              }}
              startAdornment={
                <InputAdornment position="start">{currencySign}</InputAdornment>
              }
              classes={{
                input: cn(classes.outlinedInput, { [classes.error]: error }),
                root: classes.outlinedInputRoot,
              }}
            />
          )
        } else {
          return (
            <Typography className={classes.tableValue}>
              {currencySign}
              {row[key] !== null ? row[key] : countrySettings[key]}
            </Typography>
          )
        }
      }
      case 'number': {
        const isFlyerPack = checkIsFlyerPack(key)

        let error = false

        if (isFlyerPack && row[key].error) {
          error = true
        }

        let inputValue = row[key]
        let value = row[key]

        if (
          key === WEEKLY_MISSION_LIMIT &&
          typeof row[key] !== 'number' &&
          !row[key]
        ) {
          inputValue = ''
        }

        if (isFlyerPack) {
          inputValue = row[key].value
        }

        if (isFlyerPack) {
          value = row[key].value
          if (!row[key].value) {
            value = countrySettings[key]
            error = false
          }
        } else if (row[key] !== null) {
          value = row[key]
        } else if (
          key === WEEKLY_MISSION_LIMIT &&
          typeof row[key] !== 'number' &&
          !row[key] &&
          !countrySettings[key]
        ) {
          value = 'Unlimited'
        } else {
          value = countrySettings[key]
        }

        return isEdit ? (
          <OutlinedInput
            id={`${row.name}-${key}`}
            value={inputValue}
            onChange={event => {
              const { value } = event.target

              const flyerPackH2h =
                'flyerPackH2h' === key ? { error: value < 100, value } : null
              const flyerPackD2d =
                'flyerPackD2d' === key ? { error: value < 100, value } : null

              return isValueValid(value)
                ? handleCellChange(
                    flyerPackH2h || flyerPackD2d || value,
                    row,
                    key,
                  )
                : null
            }}
            classes={{
              input: cn(classes.outlinedNumberInput, {
                [classes.error]: error,
              }),
              root: classes.outlinedInputRoot,
            }}
          />
        ) : (
          <Typography
            className={cn(classes.tableValue, {
              [classes.error]: error,
            })}
          >
            {value}
          </Typography>
        )
      }
      default:
        return <Typography>{row[key]}</Typography>
    }
  }

  const handleSave = useCallback(async () => {
    const response = await onSave(citiesSettingsValues)
    if (!response) return
    const { success, error } = response
    if (success) {
      runSnackBar({
        type: 'success',
        msg: `${title} were saved successfully`,
      })
      handleEditMode(false)
    } else {
      runSnackBar({
        type: 'error',
        msg: `There is an error while saving ${title}.`,
        error: error,
      })
    }
  }, [citiesSettingsValues])

  const handleCancel = useCallback(() => {
    const { formattedCitiesSettings, error } = formatCitiesSettings(
      citiesSettings,
      countrySettings,
    )

    setCitiesSettingsValues(formattedCitiesSettings)
    setError(error)
    handleEditMode(false)
  }, [citiesSettings])

  useEffect(() => {
    const { formattedCitiesSettings, error } = formatCitiesSettings(
      citiesSettings,
      countrySettings,
    )

    setError(error)
    setCitiesSettingsValues(formattedCitiesSettings)
  }, [])

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.header}>
        {isEdit ? (
          <Box className={classes.btnWrapper}>
            {error ? (
              <div className={classes.bigError}>{error}</div>
            ) : (
              <ButtonInline
                label={'Save'}
                labelColor={theme.colors.primary}
                icon={<SaveIcon />}
                onClickHandler={() => handleSave()}
              />
            )}
            <ButtonInline
              label={'Cancel'}
              icon={<CancelIcon />}
              onClickHandler={() => handleCancel()}
            />
          </Box>
        ) : (
          <ButtonInline
            label={'Edit'}
            icon={<EditIcon style={{ marginLeft: '7px' }} />}
            labelFirst
            onClickHandler={() => handleEditMode(true)}
          />
        )}
      </Box>

      <Box className={classes.table}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="medium">
            <TableHead className={classes.tableHead}>
              <TableRow>
                {CITY_SETTINGS_PRE_HEADERS.map(item => (
                  <TableCell
                    key={item.label}
                    align="center"
                    colSpan={item.colSpan}
                    className={cn(classes.tableHeaderCell, classes.borderRight)}
                  >
                    <Typography>{item.label}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableHead className={classes.tableHead}>
              <TableRow>
                {CITY_SETTINGS_HEADERS.map((headCell, index) => (
                  <TableCell
                    key={headCell.key}
                    align={index === 0 ? 'left' : 'center'}
                    sortDirection={orderBy === headCell.key ? order : false}
                    className={cn(
                      index !== 0
                        ? classes.tableHeaderCell
                        : classes.firstTableHeaderCell,
                      BORDER_KEYS.includes(headCell.key)
                        ? classes.borderRight
                        : {},
                    )}
                  >
                    <TableSortLabel
                      active={!isEdit && orderBy === headCell.key}
                      direction={orderBy === headCell.key ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.key)}
                    >
                      <Typography>{headCell.label}</Typography>
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(
                citiesSettingsValues,
                getComparator(order, orderBy),
              ).map(row => {
                return (
                  <TableRow hover={isEdit} tabIndex={-1} key={row.id}>
                    {CITY_SETTINGS_HEADERS.map(({ key, type }, index) => {
                      const isNoValue =
                        (checkIsFlyerPack(key) && !row[key].value) ||
                        row[key] === null

                      return (
                        <TableCell
                          align={index === 0 ? 'left' : 'center'}
                          className={cn({
                            [classes.firstTableHeaderCell]: index === 0,
                            [classes.borderRight]: BORDER_KEYS.includes(key),
                            [classes.noValue]: isNoValue,
                            [classes.padding4]:
                              type && type !== 'checkbox' && isEdit,
                          })}
                        >
                          {renderTableCell(row, key, type)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export default injectStyles(styles)(CitySettingsTable)
